// Composition grammar validator (§3.6) — the grammar IS the pedagogy.
//
// This is not a checklist afterthought; it is where the composition teaching
// knowledge lives. It runs as the `composed-experience` branch of
// validateSlideData (the generic title/subtitle/placeholder/id checks still run
// first), plus it is exported standalone for unit testing and pipeline gating.
//
// Minimum rules (handover §3.6):
//   1. Every ref points at an existing UPSTREAM block of a compatible type
//      (no orphans, no forward refs, no self-refs).
//   2. A reveal must FOLLOW and ref an actionable commit — never precede it.
//   3. At least one actionable block per experience.
//   4. The close must use at least one captured value token (no homily endings).
//   5. Sequence sanity: no experience ends on a bare input with no close.
//   6. Per-block content checks (counts, key refs, ranges) — derived from the
//      primitive registry's qaRejectRules.
// Plus: explicit, valid experience-level scoring (§3.4) and the close beat
// word-budget rule (§3.5.2).
//
// Pure module — no React, no network. Safe to import anywhere.

import type { QAIssue } from '../agents/types.js';
import { PRIMITIVE_REGISTRY } from './primitiveRegistry.js';
import { PRIMITIVE_TYPES, type CompositionBlock, type PrimitiveType } from './types.js';

export interface CompositionValidationResult {
  ok: boolean;
  criticalIssues: QAIssue[];
  warnings: QAIssue[];
}

/** Minimal issue sink — matches the Ctx shape used inside validateSlideData. */
export interface IssueSink {
  critical(location: string, issue: string, fixInstruction: string): void;
  warn(location: string, issue: string, fixInstruction: string): void;
}

type Data = Record<string, unknown>;
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asObj = (v: unknown): Data => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Data) : {});
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const ACTIONABLE_TYPES = new Set<PrimitiveType>([
  'choice',
  'rate-slider',
  'sort-zones',
  'select-evidence',
  'confidence',
  'flag-in-text',
]);
const ARTICULATION_TYPES = new Set<PrimitiveType>(['commit-box', 'perspective-lens']);

/**
 * Run the composition grammar against `data`, pushing issues into `ctx`. Shared
 * by validateSlideData (passes its Ctx) and validateComposition (passes a local
 * collector). `data` is the composed-experience slide data.
 */
export function runCompositionChecks(data: Data, ctx: IssueSink): void {
  const blocks = asArr(data.blocks) as Data[];

  if (blocks.length === 0) {
    ctx.critical('blocks', 'A composed experience has no blocks.', 'Add at least one stimulus and one actionable block.');
    return;
  }

  // Index blocks by id + record ordering for upstream/forward-ref checks.
  const indexById = new Map<string, number>();
  blocks.forEach((b, i) => {
    const id = asStr(b.id);
    if (id && !indexById.has(id)) indexById.set(id, i);
  });

  let actionableCount = 0;

  blocks.forEach((b, i) => {
    const loc = `blocks[${i}]`;
    const id = asStr(b.id) || `#${i}`;
    const type = asStr(b.type) as PrimitiveType;

    if (!PRIMITIVE_TYPES.includes(type)) {
      ctx.critical(`${loc}.type`, `Unknown primitive type "${asStr(b.type)}".`, `Use one of: ${PRIMITIVE_TYPES.join(', ')}.`);
      return; // can't check content of an unknown block
    }
    const spec = PRIMITIVE_REGISTRY[type];

    // captureClass must match the primitive's contract.
    const cc = asStr(b.captureClass);
    if (cc !== spec.captureClass) {
      ctx.critical(`${loc}.captureClass`, `Block "${id}" (${type}) declares captureClass "${cc}"; ${type} is always "${spec.captureClass}".`, `Set captureClass to "${spec.captureClass}".`);
    }
    if (spec.captureClass === 'actionable') actionableCount += 1;

    // structure/content separation must be present.
    if (!('content' in b)) {
      ctx.critical(`${loc}.content`, `Block "${id}" has no content object.`, 'Put all authored text/media under `content`.');
    }

    // ---- ref rules (§3.6 #1, #2) ------------------------------------------
    const ref = asStr(b.ref) || undefined;
    if (spec.requiresRef && !ref) {
      ctx.critical(`${loc}.ref`, `A ${type} must ref the upstream block it pairs with.`, 'Add `ref` pointing at an earlier actionable block.');
    }
    if (ref) {
      if (ref === id) {
        ctx.critical(`${loc}.ref`, `Block "${id}" refs itself.`, 'Point `ref` at a different, earlier block.');
      } else if (!indexById.has(ref)) {
        ctx.critical(`${loc}.ref`, `ref "${ref}" points at no existing block (orphan ref).`, 'Point `ref` at an existing block id.');
      } else {
        const targetIdx = indexById.get(ref)!;
        if (targetIdx >= i) {
          ctx.critical(`${loc}.ref`, `ref "${ref}" is a forward ref (target is not upstream).`, 'A block may only ref an EARLIER block.');
        } else {
          const targetType = asStr(blocks[targetIdx].type) as PrimitiveType;
          const targetSpec = PRIMITIVE_REGISTRY[targetType];
          if (targetSpec && !targetSpec.refTarget) {
            ctx.critical(`${loc}.ref`, `ref "${ref}" points at a ${targetType}, which can't be a ref target.`, 'Ref an actionable block (choice, rate-slider, sort-zones, select-evidence).');
          }
          // reveal must ref an ACTIONABLE commit (the core pedagogical invariant).
          if (type === 'reveal' && targetSpec && targetSpec.captureClass !== 'actionable') {
            ctx.critical(`${loc}.ref`, `A reveal must ref an actionable block; "${ref}" is ${targetType} (${targetSpec.captureClass}).`, 'Point the reveal at the actionable block the learner committed on.');
          }
          // A re-rating (rate-slider / confidence) delta only makes sense against
          // another block of the same type.
          if ((type === 'rate-slider' || type === 'confidence') && targetType !== type) {
            ctx.warn(`${loc}.ref`, `A ${type} re-rating usually refs another ${type}; "${ref}" is ${targetType}.`, `Ref the earlier ${type} so the delta reads back.`);
          }
        }
      }
    }

    // ---- per-block content checks (§3.6 #6) --------------------------------
    checkBlockContent(b, type, loc, id, ctx);
  });

  // ---- at least one actionable block (§3.6 #3) -----------------------------
  if (actionableCount === 0) {
    ctx.critical('blocks', 'No actionable block — an all-articulation composition is a form, not an experience.', 'Add at least one choice / rate-slider / sort-zones / select-evidence block.');
  }

  // ---- sequence sanity (§3.6 #5) -------------------------------------------
  const last = blocks[blocks.length - 1];
  const lastType = asStr(last.type) as PrimitiveType;
  const lastIsBareInput = ACTIONABLE_TYPES.has(lastType) || ARTICULATION_TYPES.has(lastType);
  if (lastIsBareInput && !data.conclusion) {
    ctx.warn('blocks', 'Experience ends on a bare input with no close.', 'Add a reveal and/or a `conclusion` close so the experience lands.');
  }

  // ---- scoring (§3.4) ------------------------------------------------------
  checkScoring(data, blocks as unknown as CompositionBlock[], indexById, ctx);

  // ---- close token rule (§3.6 #4) + beat budget (§3.5.2) -------------------
  checkClose(data, ctx);
}

function checkBlockContent(b: Data, type: PrimitiveType, loc: string, id: string, ctx: IssueSink): void {
  const content = asObj(b.content);
  const structure = asObj(b.structure);
  switch (type) {
    case 'stimulus':
      if (!asStr(content.body).trim()) ctx.critical(`${loc}.content.body`, `Stimulus "${id}" has an empty body.`, 'Give the stimulus a specific, concrete situation.');
      break;
    case 'choice': {
      const opts = asArr(content.options) as Data[];
      if (opts.length < 2) ctx.critical(`${loc}.content.options`, `Choice "${id}" has ${opts.length} option(s); needs ≥2.`, 'Provide 2–4 distinct options.');
      const correct = opts.filter((o) => o.correct === true).length;
      if (correct > 1) ctx.critical(`${loc}.content.options`, `Choice "${id}" marks ${correct} options correct.`, 'Mark at most one option correct.');
      break;
    }
    case 'rate-slider': {
      const min = structure.min;
      const max = structure.max;
      if (isNum(min) && isNum(max) && min >= max) ctx.critical(`${loc}.structure`, `rate-slider "${id}" has min ≥ max.`, 'Set min < max.');
      const target = asObj(structure.target);
      if ('target' in structure && isNum(target.value) && isNum(min) && isNum(max) && (target.value < min || target.value > max)) {
        ctx.critical(`${loc}.structure.target`, `rate-slider "${id}" target ${target.value} is outside [${min},${max}].`, 'Put the scoring target within the scale bounds.');
      }
      break;
    }
    case 'sort-zones': {
      const zones = asArr(content.zones) as Data[];
      const items = asArr(content.items) as Data[];
      if (zones.length < 2) ctx.critical(`${loc}.content.zones`, `sort-zones "${id}" has ${zones.length} zone(s); needs ≥2.`, 'Provide 2–4 labelled zones.');
      const zoneIds = new Set(zones.map((z) => asStr(z.id)));
      items.forEach((it, j) => {
        const cz = asStr(it.correctZone);
        if (cz && !zoneIds.has(cz)) ctx.critical(`${loc}.content.items[${j}].correctZone`, `Item's correctZone "${cz}" references no zone id.`, 'Point correctZone at an existing zone id.');
      });
      break;
    }
    case 'select-evidence': {
      const items = asArr(content.items) as Data[];
      const strong = items.filter((it) => it.strong === true).length;
      const distractors = items.length - strong;
      if (strong < 1) ctx.critical(`${loc}.content.items`, `select-evidence "${id}" flags no strong item.`, 'Flag at least one item strong:true (the expert set).');
      if (distractors < 2) ctx.warn(`${loc}.content.items`, `select-evidence "${id}" has ${distractors} distractor(s).`, 'Add ≥2 plausible-but-weak distractors so there is evaluation to do.');
      break;
    }
    case 'reveal':
      if (!asStr(content.body).trim()) ctx.critical(`${loc}.content.body`, `Reveal "${id}" has an empty body.`, 'Provide the expert view that contrasts with the learner’s answer.');
      break;
    case 'commit-box': {
      if (!asStr(content.prompt).trim()) ctx.critical(`${loc}.content.prompt`, `commit-box "${id}" has an empty prompt.`, 'Ask for a specific, constrained commitment.');
      const minW = structure.minWords;
      const maxW = structure.maxWords;
      if (isNum(minW) && isNum(maxW) && minW > maxW) ctx.critical(`${loc}.structure`, `commit-box "${id}" has minWords > maxWords.`, 'Fix the word bounds.');
      break;
    }
    case 'confidence': {
      const min = structure.min;
      const max = structure.max;
      if (isNum(min) && isNum(max) && min >= max) ctx.critical(`${loc}.structure`, `confidence "${id}" has min ≥ max.`, 'Set min < max.');
      break;
    }
    case 'flag-in-text': {
      const segs = asArr(content.segments) as Data[];
      const flagged = segs.filter((s) => s.flagged === true).length;
      const honest = segs.length - flagged;
      if (flagged === 0) ctx.critical(`${loc}.content.segments`, `flag-in-text "${id}" flags no segment.`, 'Mark the wrong/weak spans flagged:true.');
      if (flagged === segs.length && segs.length > 0) ctx.warn(`${loc}.content.segments`, `flag-in-text "${id}" flags every segment.`, 'Leave honest segments so catching the flaws takes real reading.');
      // Enough decoys: every segment is clickable with the same cursor, so the
      // challenge only works if honest text outnumbers the flaws — otherwise the
      // answers are guessable without reading.
      else if (flagged > 0 && (honest < flagged || honest < 2)) {
        ctx.warn(`${loc}.content.segments`, `flag-in-text "${id}" has only ${honest} honest segment(s) against ${flagged} flagged.`, 'Add more honest (unflagged) segments — aim for honest ≥ flagged and at least 2 decoys, so catching the flaws takes real reading.');
      }
      // Consistent granularity: if the flagged spans are cut much shorter than the
      // honest text (or vice-versa), the answers stand out by shape even though
      // every span is clickable — the affordance leaks through segment length.
      if (flagged > 0 && honest > 0) {
        const avg = (arr: Data[]) => arr.reduce((n, s) => n + asStr(s.text).trim().length, 0) / (arr.length || 1);
        const flaggedAvg = avg(segs.filter((s) => s.flagged === true));
        const honestAvg = avg(segs.filter((s) => s.flagged !== true));
        const ratio = Math.max(flaggedAvg, honestAvg) / Math.max(1, Math.min(flaggedAvg, honestAvg));
        if (ratio >= 4) ctx.warn(`${loc}.content.segments`, `flag-in-text "${id}" cuts flagged and honest spans at very uneven lengths (≈${Math.round(flaggedAvg)} vs ${Math.round(honestAvg)} chars).`, 'Segment the whole passage at a consistent granularity so the answers do not stand out by shape.');
      }
      break;
    }
    case 'perspective-lens': {
      const lenses = asArr(content.lenses) as Data[];
      if (lenses.length < 2) ctx.warn(`${loc}.content.lenses`, `perspective-lens "${id}" has ${lenses.length} lens(es).`, 'Give 2–4 genuinely distinct lenses.');
      break;
    }
  }
}

function checkScoring(data: Data, blocks: CompositionBlock[], indexById: Map<string, number>, ctx: IssueSink): void {
  if (!('scoring' in data)) {
    ctx.critical('scoring', 'Composed experience has no scoring object (grade semantics are undefined).', 'Add a `scoring` object declaring which actionable blocks count and how.');
    return;
  }
  const scoring = asObj(data.scoring);
  const mode = asStr(scoring.mode);
  if (!['mean', 'weighted', 'single', 'none'].includes(mode)) {
    ctx.critical('scoring.mode', `Unknown scoring mode "${mode}".`, 'Use "mean", "weighted", "single", or "none".');
  }
  const entries = asArr(scoring.blocks) as Data[];
  // `none` = the experience captures positions but isn't graded (values /
  // opinion / reflection). No blocks to score; anything listed is ignored.
  if (mode === 'none') {
    if (entries.length > 0) {
      ctx.warn('scoring.blocks', 'mode "none" ignores scoring.blocks.', 'Clear the list, or pick mean/weighted/single to grade.');
    }
    return;
  }
  if (entries.length === 0) {
    ctx.critical('scoring.blocks', 'scoring.blocks is empty.', 'List the actionable blocks that count toward the grade, or use mode "none" for an ungraded experience.');
  }
  if (mode === 'single' && entries.length !== 1) {
    ctx.critical('scoring.blocks', `single-mode scoring must list exactly 1 block; got ${entries.length}.`, 'List exactly one block, or switch mode to "mean"/"weighted".');
  }
  entries.forEach((e, i) => {
    const bid = asStr(e.id);
    const idx = bid ? indexById.get(bid) : undefined;
    if (idx === undefined) {
      ctx.critical(`scoring.blocks[${i}]`, `scoring references block "${bid}" which doesn't exist.`, 'Point every scoring entry at an existing block id.');
      return;
    }
    const block = blocks[idx];
    if (block.captureClass !== 'actionable') {
      ctx.critical(`scoring.blocks[${i}]`, `scoring counts "${bid}" (${block.type}, ${block.captureClass}); only actionable blocks may score.`, 'Remove it, or score a different block.');
    } else if (!blockIsScoreable(block)) {
      ctx.critical(`scoring.blocks[${i}]`, `scoring counts "${bid}" (${block.type}) but it has no correct answer / target to grade against.`, 'Give the block a correct answer/target, or drop it from scoring.');
    }
    if (mode === 'weighted' && !isNum(e.weight)) {
      ctx.warn(`scoring.blocks[${i}].weight`, `weighted mode but "${bid}" has no numeric weight (defaults to 1).`, 'Give each counted block an explicit weight.');
    }
  });
}

/** Local re-implementation of isScoreable over raw data (blocks come through as Data). */
function blockIsScoreable(block: CompositionBlock): boolean {
  const b = block as unknown as Data;
  const content = asObj(b.content);
  const structure = asObj(b.structure);
  switch (block.type) {
    case 'choice':
      return asArr(content.options).some((o) => asObj(o).correct === true);
    case 'sort-zones':
      return asArr(content.items).length > 0;
    case 'select-evidence':
      return asArr(content.items).some((i) => asObj(i).strong === true);
    case 'rate-slider':
      return 'target' in structure;
    case 'flag-in-text':
      return asArr(content.segments).some((s) => asObj(s).flagged === true);
    default:
      return false;
  }
}

// Token forms the close may reference: `{blockId.token}` (block-scoped) or a bare
// `{token}` (legacy flat). Presence of ANY captured-value token satisfies #4.
function checkClose(data: Data, ctx: IssueSink): void {
  if (!data.conclusion) return; // optional during rollout; sequence-sanity warns separately
  const c = asObj(data.conclusion);

  // Collect every authored close string (top-level, variants, beats).
  const texts: string[] = [];
  const pushIf = (v: unknown) => {
    const s = asStr(v);
    if (s) texts.push(s);
  };
  pushIf(c.heading);
  pushIf(c.body);
  asArr(c.variants).forEach((v) => pushIf(asObj(v).body));
  const beats = asArr(c.beats) as Data[];
  beats.forEach((b) => pushIf(b.body));

  // #4 — the close must reference at least one captured value token
  // (block ids are kebab-case, so tokens may contain hyphens).
  const usesToken = texts.some((t) => /\{[\w.-]+\}/.test(t));
  if (!usesToken) {
    ctx.warn('conclusion', 'Close uses no captured value token (risk of a homily ending).', 'Reference at least one {block.token} so the close reflects the learner’s own values back.');
  }

  // §3.5.2 — no wall-of-text endings: each beat ≤ ~60 words.
  beats.forEach((b, i) => {
    const wc = asStr(b.body).trim().split(/\s+/).filter(Boolean).length;
    if (wc > 60) {
      ctx.warn(`conclusion.beats[${i}]`, `Beat is ${wc} words (budget ~60).`, 'Split into more beats or trim; a beat presents one idea, learner-paced.');
    }
  });
}

/** Standalone entry — collects issues instead of pushing into a shared Ctx. */
export function validateComposition(data: unknown): CompositionValidationResult {
  const criticalIssues: QAIssue[] = [];
  const warnings: QAIssue[] = [];
  const sink: IssueSink = {
    critical: (location, issue, fixInstruction) => criticalIssues.push({ location, issue, fixInstruction }),
    warn: (location, issue, fixInstruction) => warnings.push({ location, issue, fixInstruction }),
  };
  runCompositionChecks(asObj(data), sink);
  return { ok: criticalIssues.length === 0, criticalIssues, warnings };
}
